import React, { useEffect, useState } from 'react';
import { Card, Col, Row, Spinner } from "reactstrap";
import LogoLRF from "../../AppSystem/assets/img/LRF-new-Logga.png";
import { useTranslation } from "react-i18next";
import i18next from 'i18next';
import i18n from 'i18next';
import { LoadLocalStorage } from "../Helpers/LoadLocalStorage";
import { CheckUserPermission } from '../../AppSystem/Helpers/CheckUserPermission';
import useApiCallService from '../Helpers/ApiCall';
import DashboardResponse from '../Models/Response/DashboardResponse';
import { API_URL, MAX_FILE_SIZE_BYTES } from '../../config';
import ReactQuill, { Quill } from 'react-quill';
import { CustomButton } from '../Components/Button/CustomButton';
import { FlashMessage } from '../Helpers/FlashMessage';
import DashboardRequest from '../Models/Request/DashboardRequest';
import { useSelector } from 'react-redux';
import { SaveLocalStorage } from '../Helpers/SaveLocalStorage';
import { CheckMobile } from '../../AppSystem/Helpers/CheckMobile';
import { SelectOrganization } from '../Components/SelectOrganization/SelectOrganization';
import { SlidePanel } from '../Components/Modal/SlidePanel';
import { Link, useNavigate } from 'react-router-dom';
import { REPORT_PROCEEDING_PATHS, PAY_PATHS, ORGANIZATION_PATHS } from '../../config';

// ── Blot personalizado para iframes — permite cualquier URL embebida ──
const BlockEmbed = Quill.import('blots/block/embed');
class VideoBlot extends BlockEmbed {
    static create(value) {
        const node = super.create();

        // Si es Vimeo, añade parámetros de bucle sin UI
        let src = value;
        if (/vimeo\.com/.test(src)) {
            const url = new URL(src.startsWith('http') ? src : 'https:' + src);
            if (!url.searchParams.has('autoplay')) url.searchParams.set('autoplay', '0');
            if (!url.searchParams.has('loop')) url.searchParams.set('loop', '1');
            src = url.toString();
        }

        node.setAttribute('src', src);
        node.setAttribute('frameborder', '0');
        node.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
        node.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
        node.setAttribute('allowfullscreen', true);
        node.style.width = '100%';
        node.style.aspectRatio = '16/9';
        node.style.display = 'block';
        node.style.marginBottom = '0.5rem';
        return node;
    }
    static value(node) {
        return node.getAttribute('src');
    }
}
VideoBlot.blotName = "video";
VideoBlot.tagName = "iframe";
Quill.register(VideoBlot, true);

export function Dashboard() {
    const { ApiCall } = useApiCallService();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const isMobile = CheckMobile(998);
    const { userData } = useSelector(state => state.auth);

    const [executeEffect, setExecuteEffect] = useState(false);
    const [apiResponse, setApiResponse] = useState(new DashboardResponse());
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [fields, setFields] = useState(new DashboardRequest());
    const [value, setValue] = useState('');
    const [expanded, setExpanded] = useState(false);
    const [slidePanelOpen, setSlidePanelOpen] = useState(false);
    const [displayExpanded, setDisplayExpanded] = useState(false);

    let permissionUpdate = CheckUserPermission("system");

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        if (i18next.language !== LoadLocalStorage('selectedLanguage')) {
            i18n.changeLanguage(LoadLocalStorage('selectedLanguage'));
        }
        if (!executeEffect) setExecuteEffect(true);
    }, [executeEffect]);

    useEffect(() => {
        let isMounted = true;

        const handleApiResponse = (response) => {
            if (!isMounted) return;
            if (response) { setApiResponse(response); setValue(response.body); }
        };

        const handleViewSettingResponse = (response) => {
            if (response?.viewSettings?.length > 0) {
                SaveLocalStorage("viewSetting", JSON.stringify(response.viewSettings));
            }
        };

        ApiCall({ method: "get", url: "/dashboard" }, handleApiResponse).catch(console.log);
        ApiCall({ method: "get", url: "/usersetting/view-settings" }, handleViewSettingResponse).catch(console.log);

        return () => { isMounted = false; };
    }, []);

    const extractBase64Images = (html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const images = Array.from(doc.querySelectorAll("img"));
        const base64Images = [];
        images.forEach((img) => {
            const src = img.getAttribute("src");
            if (src?.startsWith("data:image")) base64Images.push({ base64: src, node: img });
        });
        return { doc, base64Images };
    };

    const base64ToFile = (base64, filename = 'image.jpg') => {
        const arr = base64.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) u8arr[n] = bstr.charCodeAt(n);
        return new File([u8arr], filename, { type: mime });
    };

    const uploadImagesAndReplace = async (html) => {
        const { doc, base64Images } = extractBase64Images(html);
        let uploadedImageIds;
        for (const { base64, node } of base64Images) {
            try {
                const file = base64ToFile(base64);
                if (file.size > MAX_FILE_SIZE_BYTES) {
                    FlashMessage(t('validationMessage.maxSizeFile'), 'flash-messageWarning');
                    return;
                }
                const formData = new FormData();
                formData.append('file', file);
                await ApiCall({ method: "post", url: "/file", data: formData, dataTypes: "file" }, (res) => {
                    if (res?.id) {
                        node.setAttribute("src", `${API_URL}/file?id=${res.id}`);
                        uploadedImageIds = res.id;
                    }
                });
            } catch {
                FlashMessage("validationMessage.sendError", "flash-messageError");
            }
        }
        return { html: doc.body.innerHTML, imageIds: uploadedImageIds };
    };

    const handleSave = async (val) => {
        setLoading(true);
        setIsEditing(false);
        const { html, imageId } = await uploadImagesAndReplace(val);
        try {
            await ApiCall({ method: "put", url: "/dashboard", data: { ...fields, body: html, fileId: imageId } }, setApiResponse);
        } catch {
            FlashMessage(t('validationMessage.sendError'), 'flash-messageError');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = () => { setIsEditing(false); handleSave(value); };

    const modules = {
        toolbar: {
            container: [
                [{ 'header': [1, 2, false] }],
                ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
                ['link', 'image', 'video'],
                ['clean']
            ],
        },
    };
    const formats = ['header', 'bold', 'italic', 'underline', 'strike', 'blockquote', 'list', 'bullet', 'indent', 'link', 'image', 'video'];

    const getPreview = (html) => {
        if (!html) return { previewHtml: '' };
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        
        const blocks = Array.from(doc.body.querySelectorAll('h1, h2, h3, h4, h5, h6, p, li, blockquote'))
            .filter(el => !el.querySelector('img') && el.textContent.trim() !== '');
        
        const first2 = blocks.slice(0, 2).map(el => el.outerHTML).join('');
        
        return { previewHtml: first2 };
    };

    const handleToggle = () => {
        if (!expanded) {
            setDisplayExpanded(true);
            setTimeout(() => setExpanded(true), 10);
        } else {
            setExpanded(false);
            setTimeout(() => setDisplayExpanded(false), 400);
        }
    };

    // ── Helper: normalize images, iframes, and videos ──
    const renderContent = (html) => {
        if (!html) return '';
        let result = html.replace(
            /<img /g,
            '<img style="max-width:100%;height:auto;display:block;margin:0.5rem 0;" '
        );
        result = result.replace(
            /<iframe /g,
            '<iframe referrerpolicy="strict-origin-when-cross-origin" style="width:100%;aspect-ratio:16/9;display:block;margin:0.5rem 0;" '
        );
        result = result.replace(
            /<video /g,
            '<video style="width:100%;height:auto;display:block;margin:0.5rem 0;" controls '
        );
        return result;
    };

    // ── EDITOR (móvil y desktop comparten este bloque) ──
    if (isEditing) {
        return (
            <Col lg={6} className='pb-5 offset-md-3'>
                {apiResponse ? (
                    <Card className='card-backend p-3 p-lg-5'>
                        <Row className='justify-content-center'>
                            <Col lg={12}>
                                <ReactQuill modules={modules} formats={formats} theme="snow" value={value} onChange={setValue} />
                                <Col xs="auto" className="text-end mt-3">
                                    <CustomButton
                                        icon="bx bx-x py-1" iconColor='#EB5757' iconSize={5}
                                        buttonColor='#e9cbcd' buttonRadius={3} hoverButtonRadius={3}
                                        hoverIconColor="white" hoverButtonColor='#EB5757'
                                        customClasses="edit-button me-2" onClick={() => setIsEditing(false)}
                                        tooltipText={t("button.cancel")}
                                    />
                                    <CustomButton
                                        icon="bx bx-save py-1" iconColor="#157553" iconSize={5}
                                        buttonColor="#bed1cd" buttonRadius={3} hoverButtonRadius={3}
                                        hoverIconColor="white" hoverButtonColor="#157553"
                                        customClasses="edit-button" onClick={handleChange}
                                        tooltipText={t("button.save")}
                                    />
                                </Col>
                            </Col>
                            <Col className='justify-content-center col-auto'>
                                <img src={LogoLRF} style={{ maxWidth: '200px' }} alt="logo" />
                            </Col>
                        </Row>
                    </Card>
                ) : (
                    <Card className='card-backend p-3 p-lg-5 d-flex justify-content-center align-items-center' style={{ height: '200px' }}>
                        <Spinner color='success' style={{ width: '4rem', height: '4rem' }} />
                    </Card>
                )}
            </Col>
        );
    }

    // ── VISTA MÓVIL ──
    if (isMobile) {
        const { previewHtml } = getPreview(value);

        return (
            <div className="dashboardMobileWrapper w-100">
                <div className="dashboardMobileGreeting">
                    <h2>{t('modelLabel.hello', 'Hej')} {userData?.firstName},</h2>
                </div>
                <div className="dashboardMobileOrgSelector">
                    <SelectOrganization />
                </div>
                <div className="dashboardMobileCardWrapper">
                    {apiResponse?.fileId ? (
                        <div className="dashboardMobileCard">
                            {permissionUpdate && (
                                <div style={{ position: 'absolute', top: 12, right: 12 }}>
                                    <CustomButton
                                        icon="bx bxs-pencil py-1" iconColor="#157553" iconSize={5}
                                        buttonColor="#bed1cd" buttonRadius={3} hoverButtonRadius={3}
                                        hoverIconColor="white" hoverButtonColor="#157553"
                                        customClasses="edit-button" onClick={() => setIsEditing(true)}
                                        tooltipText={t("button.edit")}
                                    />
                                </div>
                            )}
                            <div className="dashboardMobileCardContent" style={{ maxHeight: expanded ? 2000 : 120 }}>
                                <div
                                    className="dashboardMobileCardText"
                                    dangerouslySetInnerHTML={{
                                        __html: displayExpanded ? renderContent(value) : previewHtml
                                    }}
                                />
                            </div>
                            <div className="dashboardMobileCardToggle">
                                <button className="dashboardMobileCardToggleBtn" onClick={handleToggle}>
                                    {expanded ? t('button.readLess', 'läs mindre') : t('button.readMore', 'läs mer')}
                                </button>
                                <i className={`bx ${expanded ? 'bx-chevron-up' : 'bx-chevron-down'} dashboardMobileCardToggleIcon`} onClick={handleToggle} />
                            </div>
                        </div>
                    ) : (
                        <div className="dashboardMobileSpinner">
                            <Spinner color='success' style={{ width: '3rem', height: '3rem' }} />
                        </div>
                    )}
                </div>
                <div className="dashboardMobileSnabbval">
                    <h6 className="dashboardMobileSnabbvalTitle">{t('quickAccess.title', 'Snabbval')}</h6>
                    <div className="dashboardMobileSnabbvalGrid">
                        <div className="dashboardMobileSnabbvalBtnGreen" onClick={() => {
                                SaveLocalStorage('headerTitle', t('quickAccess.ersattning', 'Ersättningsärenden'));
                                navigate(REPORT_PROCEEDING_PATHS.REPORT_PROCEEDING_LIST);
                            }}
                        >
                            <i className="bx bx-receipt" style={{ fontSize: 32 }} />
                            <span>{t('quickAccess.ersattning', 'Ersättningsärenden')}</span>
                        </div>
                        <div onClick={() => setSlidePanelOpen(true)} className="dashboardMobileSnabbvalBtnLight">
                            <i className="bx bx-dollar-circle" style={{ fontSize: 32 }} />
                            <span>{t('quickAccess.samlain', 'Samla in')}</span>
                        </div>
                    </div>
                </div>
                <SlidePanel
                    isOpen={slidePanelOpen}
                    onClose={() => setSlidePanelOpen(false)}
                    title={t('quickAccess.samlain', 'Samla in')}
                >
                    <div className="dashboardSlidePanelLink" 
                        onClick={() => { SaveLocalStorage('headerTitle', t('quickAccess.swedbankpay', 'Snabba betalningar')); setSlidePanelOpen(false); navigate(PAY_PATHS.SWEDBANKPAY); }}
                    >
                        <span>{t('quickAccess.swedbankpay', 'Snabba betalningar')}</span>
                        <i className="bx bx-chevron-right" style={{ fontSize: 20, color: '#adb5bd' }} />
                    </div>
                    <div
                        onClick={() => { SaveLocalStorage('headerTitle', t('quickAccess.qrCode', 'QR-koder')); setSlidePanelOpen(false); navigate(ORGANIZATION_PATHS.QR_CODE_LIST); }}
                        className="dashboardSlidePanelLink"
                    >
                        <span>{t('quickAccess.qrCode', 'QR-koder')}</span>
                        <i className="bx bx-chevron-right" style={{ fontSize: 20, color: '#adb5bd' }} />
                    </div>
                    <div
                        onClick={() => { SaveLocalStorage('headerTitle', t('quickAccess.invoiceList', 'Kundfaktura')); setSlidePanelOpen(false); navigate(ORGANIZATION_PATHS.INVOICE_LIST); }}
                        className="dashboardSlidePanelLink"
                    >
                        <span>{t('quickAccess.invoiceList', 'Kundfaktura')}</span>
                        <i className="bx bx-chevron-right" style={{ fontSize: 20, color: '#adb5bd' }} />
                    </div>
                </SlidePanel>
            </div>
        );
    }

    // ── VISTA DESKTOP ──
    return (
        <Col lg={6} className='pb-5 offset-md-3'>
            {apiResponse?.fileId ? (
                <Card className='card-backend p-3 p-lg-4'>
                    {permissionUpdate && (
                        <Col xs="auto" className="text-end">
                            <CustomButton
                                icon="bx bxs-pencil py-1" iconColor="#157553" iconSize={5}
                                buttonColor="#bed1cd" buttonRadius={3} hoverButtonRadius={3}
                                hoverIconColor="white" hoverButtonColor="#157553"
                                customClasses="edit-button" onClick={() => setIsEditing(true)}
                                tooltipText={t("button.edit")}
                            />
                        </Col>
                    )}
                    <Col className='p-3 p-lg-3'>
                        <Row className='justify-content-center'>
                            <Col lg={12}>
                                <div
                                    style={{ textAlign: 'left', direction: 'ltr', maxWidth: '100%' }}
                                    dangerouslySetInnerHTML={{ __html: renderContent(value) }}
                                />
                            </Col>
                            <Col className='justify-content-center col-auto'>
                                <img src={LogoLRF} style={{ maxWidth: '200px' }} alt="logo" />
                            </Col>
                        </Row>
                    </Col>
                </Card>
            ) : (
                <Card className='card-backend p-3 p-lg-5 d-flex justify-content-center align-items-center' style={{ height: '200px' }}>
                    <Spinner color='success' style={{ width: '4rem', height: '4rem' }} />
                </Card>
            )}
        </Col>
    );
}
